/**
 * "View project as customer" - mint a link into the real client portal.
 *
 * The first build of this feature drew the customer's data inside a CRM dialog.
 * It answered a narrower question than the one operators actually ask: it could
 * show what the customer WOULD see, not what the page looks like when they open
 * it - the portal's own layout, its own wording, its own empty states.
 *
 * So the entry now mints a short-lived token and sends the operator to
 * `portal.webadmin.ee` with it. The token names a client; the operator's own
 * session and `view:crm` are what authorise the look, and the portal host
 * already carries that session because the cookie's `Domain` covers both hosts.
 * A link that leaks is worth nothing to anybody who is not already staff.
 *
 * Read-only, and enforced on the server rather than promised here: every non-GET
 * under `/api/portal/` is refused while a preview is in force, so an operator
 * cannot answer an estimate, sign a contract or open a ticket AS THE CUSTOMER by
 * clicking something. See `apps/api/src/modules/portal/lib/preview.ts`.
 */
import { api } from "./api.js";

/** One person at the client company, and whether this project reaches them. */
export interface PreviewViewer {
  id: string;
  name: string;
  email: string | null;
  /** They have a portal login linked. Without one, nothing else matters. */
  hasPortalAccess: boolean;
  /** Their portal grant includes projects. Meaningless without the above. */
  projectsModule: boolean;
}

/**
 * Whether anybody could actually open this page, and if not, why.
 *
 * The link can be perfect and still lead nowhere: an archived project, or a
 * client where nobody holds portal access with the projects module on. The
 * operator finds that out beside the button they just pressed rather than by
 * reading a portal that renders correctly and means nothing.
 *
 * It is information, never a gate - the preview is minted either way, because
 * "here is the page, and here is why nobody can reach it" is more useful than a
 * refusal.
 */
export interface PreviewReach {
  visible: boolean;
  /** Named apart from `visible`, because the remedy differs: unarchive, versus
   *  invite somebody or switch their projects module on. */
  archived: boolean;
  viewers: PreviewViewer[];
}

export interface PortalPreview {
  /** The absolute portal URL, token included. Open it, do not store it. */
  url: string;
  expiresAt: string;
  reach: PreviewReach;
}

/**
 * Mint one.
 *
 * POST rather than GET because it writes a row and hands out a capability, and a
 * GET that mints credentials is one a prefetcher or a link scanner can fire by
 * accident. 404 for a project that does not exist; nothing else refuses.
 */
export const createPortalPreview = (projectId: string) =>
  api.post<PortalPreview>(`/admin/crm/projects/${projectId}/portal-preview`);
